<script setup>
import { ref, computed, onMounted } from 'vue';
import PageLayout from 'dashboard/components-next/captain/PageLayout.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import CaptainMonitor from 'dashboard/api/captain/monitor';

const isLoading = ref(true);
const hasError = ref(false);
const errorMessage = ref('');
const selectedPeriod = ref('24h');
const dashboardData = ref(null);

const periods = [
  { value: '1h', label: 'Last 1 hour' },
  { value: '6h', label: 'Last 6 hours' },
  { value: '24h', label: 'Last 24 hours' },
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
];

const qualityData = computed(() => dashboardData.value?.quality || {});
const toolHealth = computed(() => dashboardData.value?.tool_health || {});
const stats = computed(() => dashboardData.value?.stats || {});
const flaggedData = computed(
  () => dashboardData.value?.flagged_conversations || {}
);

const overallScore = computed(() => {
  const score = qualityData.value?.overall?.average_score;
  return score != null ? score : stats.value?.avg_quality_score || 0;
});

const overallScoreColor = computed(() => {
  const score = overallScore.value;
  if (score >= 8.5) return 'text-n-teal-text';
  if (score >= 7) return 'text-n-blue-text';
  if (score >= 5.5) return 'text-n-amber-text';
  return 'text-n-ruby-text';
});

const overallScoreBg = computed(() => {
  const score = overallScore.value;
  if (score >= 8.5) return 'bg-n-teal-3';
  if (score >= 7) return 'bg-n-blue-3';
  if (score >= 5.5) return 'bg-n-amber-3';
  return 'bg-n-ruby-3';
});

const overallScoreLabel = computed(() => {
  const score = overallScore.value;
  if (score >= 8.5) return 'Excellent';
  if (score >= 7) return 'Good';
  if (score >= 5.5) return 'Acceptable';
  if (score >= 4) return 'Needs Improvement';
  return 'Critical';
});

function formatToolName(name) {
  return name
    .replace(/^custom_/, '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());
}

function dimensionBarColor(score) {
  if (score >= 8.5) return 'bg-n-teal-9';
  if (score >= 7) return 'bg-n-blue-9';
  if (score >= 5.5) return 'bg-n-amber-9';
  return 'bg-n-ruby-9';
}

function toolHealthColor(pct) {
  if (pct >= 95) return 'text-n-teal-text';
  if (pct >= 80) return 'text-n-amber-text';
  return 'text-n-ruby-text';
}

function toolHealthBg(pct) {
  if (pct >= 95) return 'bg-n-teal-3';
  if (pct >= 80) return 'bg-n-amber-3';
  return 'bg-n-ruby-3';
}

function toolHealthDot(pct) {
  if (pct >= 95) return 'bg-n-teal-9';
  if (pct >= 80) return 'bg-n-amber-9';
  return 'bg-n-ruby-9';
}

const dimensions = computed(() => {
  const dims = qualityData.value?.dimensions || {};
  const dimensionConfig = [
    { key: 'resolution', label: 'Resolution', icon: 'i-lucide-check-circle' },
    { key: 'accuracy', label: 'Accuracy', icon: 'i-lucide-target' },
    { key: 'tone', label: 'Tone', icon: 'i-lucide-heart' },
    { key: 'efficiency', label: 'Efficiency', icon: 'i-lucide-zap' },
    { key: 'tool_usage', label: 'Tool Usage', icon: 'i-lucide-wrench' },
    { key: 'safety', label: 'Safety', icon: 'i-lucide-shield' },
  ];

  return dimensionConfig.map(dim => ({
    ...dim,
    score: dims[dim.key] ?? 0,
    percentage: ((dims[dim.key] ?? 0) / 10) * 100,
  }));
});

const trendDirection = computed(() => {
  return qualityData.value?.trends?.direction || 'insufficient_data';
});

const trendChange = computed(() => {
  return qualityData.value?.trends?.change || 0;
});

const trendIcon = computed(() => {
  const dir = trendDirection.value;
  if (dir === 'improving') return 'i-lucide-trending-up';
  if (dir === 'declining') return 'i-lucide-trending-down';
  return 'i-lucide-minus';
});

const trendColor = computed(() => {
  const dir = trendDirection.value;
  if (dir === 'improving') return 'text-n-teal-text';
  if (dir === 'declining') return 'text-n-ruby-text';
  return 'text-n-slate-11';
});

const trendLabel = computed(() => {
  const dir = trendDirection.value;
  if (dir === 'improving') return `+${trendChange.value} improving`;
  if (dir === 'declining') return `${trendChange.value} declining`;
  if (dir === 'insufficient_data') return 'Not enough data';
  return 'Stable';
});

const needsReviewCount = computed(() => {
  return (
    qualityData.value?.overall?.needs_review_total ||
    stats.value?.flagged_for_review ||
    0
  );
});

const toolEntries = computed(() => {
  const tools = toolHealth.value;
  if (!tools || typeof tools !== 'object') return [];

  return Object.entries(tools)
    .map(([name, data]) => ({
      name: formatToolName(name),
      rawName: name,
      success: data.success || 0,
      failure: data.failure || 0,
      total: data.total || 0,
      healthPct: data.health_pct ?? 100,
      lastSeen: data.last_seen,
    }))
    .sort((a, b) => a.healthPct - b.healthPct);
});

const totalToolCalls = computed(() => {
  return toolEntries.value.reduce((acc, entry) => acc + entry.total, 0);
});

const totalToolFailures = computed(() => {
  return toolEntries.value.reduce((acc, entry) => acc + entry.failure, 0);
});

const escalationCount = computed(() => {
  const handoffs = flaggedData.value?.handoff_events || [];
  return handoffs.length;
});

const escalationRate = computed(() => {
  const total = stats.value?.total_conversations || 0;
  if (total === 0) return 0;
  return ((escalationCount.value / total) * 100).toFixed(1);
});

const escalationReasons = computed(() => {
  const handoffs = flaggedData.value?.handoff_events || [];
  const reasons = {};

  handoffs.forEach(event => {
    const excerpt = event.excerpt || '';
    let reason = 'Unknown';

    if (/chargeback|dispute/i.test(excerpt)) reason = 'Chargeback threat';
    else if (/cancel|retention/i.test(excerpt)) reason = 'Cancellation';
    else if (/refund/i.test(excerpt)) reason = 'Refund escalation';
    else if (/fraud/i.test(excerpt)) reason = 'Fraud detection';
    else if (/error|fail/i.test(excerpt)) reason = 'Tool failure';
    else if (/angry|frustrat|upset/i.test(excerpt))
      reason = 'Customer frustration';
    else if (/safety|crisis/i.test(excerpt)) reason = 'Safety concern';

    reasons[reason] = (reasons[reason] || 0) + 1;
  });

  return Object.entries(reasons)
    .map(([reason, count]) => ({ reason, count }))
    .sort((a, b) => b.count - a.count);
});

async function fetchData() {
  isLoading.value = true;
  hasError.value = false;
  errorMessage.value = '';

  try {
    const response = await CaptainMonitor.get({
      period: selectedPeriod.value,
    });
    dashboardData.value = response.data;
  } catch (error) {
    hasError.value = true;
    errorMessage.value =
      error?.response?.data?.error || error.message || 'Failed to load data';
  } finally {
    isLoading.value = false;
  }
}

function handlePeriodChange(event) {
  selectedPeriod.value = event.target.value;
  fetchData();
}

onMounted(() => {
  fetchData();
});
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template -->
  <PageLayout
    :is-fetching="false"
    :show-pagination-footer="false"
    :show-know-more="false"
    header-title="Performance"
  >
    <template #body>
      <!-- Period selector -->
      <div class="flex items-center justify-between mb-6">
        <div class="flex items-center gap-3">
          <span class="text-sm text-n-slate-11">Time period:</span>
          <select
            :value="selectedPeriod"
            class="rounded-lg border border-n-weak bg-n-solid-2 px-3 py-1.5 text-sm text-n-slate-12 outline-none focus:border-n-brand focus:ring-1 focus:ring-n-brand"
            @change="handlePeriodChange"
          >
            <option
              v-for="period in periods"
              :key="period.value"
              :value="period.value"
            >
              {{ period.label }}
            </option>
          </select>
        </div>
        <button
          class="flex items-center gap-2 rounded-lg border border-n-weak px-3 py-1.5 text-sm text-n-slate-11 hover:bg-n-solid-3 transition-colors"
          :disabled="isLoading"
          @click="fetchData"
        >
          <span
            class="i-lucide-refresh-cw size-3.5"
            :class="{ 'animate-spin': isLoading }"
          />
          Refresh
        </button>
      </div>

      <!-- Loading state -->
      <div v-if="isLoading" class="flex items-center justify-center py-20">
        <Spinner />
      </div>

      <!-- Error state -->
      <div
        v-else-if="hasError"
        class="flex flex-col items-center justify-center gap-4 py-20"
      >
        <span class="i-lucide-alert-triangle size-10 text-n-ruby-text" />
        <p class="text-sm text-n-slate-11">
          {{ errorMessage }}
        </p>
        <button
          class="rounded-lg bg-n-brand px-4 py-2 text-sm font-medium text-white hover:brightness-110 transition"
          @click="fetchData"
        >
          Try Again
        </button>
      </div>

      <!-- Dashboard content -->
      <div v-else class="flex flex-col gap-6 pb-8">
        <!-- Stats bar -->
        <div class="grid grid-cols-4 gap-4">
          <div class="flex flex-col gap-1 rounded-xl border border-n-weak p-4">
            <span
              class="text-xs font-medium uppercase tracking-wider text-n-slate-10"
            >
              Conversations
            </span>
            <span class="text-2xl font-semibold text-n-slate-12">
              {{ stats.total_conversations || 0 }}
            </span>
          </div>
          <div class="flex flex-col gap-1 rounded-xl border border-n-weak p-4">
            <span
              class="text-xs font-medium uppercase tracking-wider text-n-slate-10"
            >
              Error Rate
            </span>
            <span class="text-2xl font-semibold text-n-slate-12">
              {{ stats.error_rate || 0 }}%
            </span>
          </div>
          <div class="flex flex-col gap-1 rounded-xl border border-n-weak p-4">
            <span
              class="text-xs font-medium uppercase tracking-wider text-n-slate-10"
            >
              Tool Failures
            </span>
            <span class="text-2xl font-semibold text-n-slate-12">
              {{ stats.tool_failures || 0 }}
            </span>
          </div>
          <div class="flex flex-col gap-1 rounded-xl border border-n-weak p-4">
            <span
              class="text-xs font-medium uppercase tracking-wider text-n-slate-10"
            >
              Flagged
            </span>
            <span class="text-2xl font-semibold text-n-slate-12">
              {{ stats.flagged_for_review || 0 }}
            </span>
          </div>
        </div>

        <!-- SECTION 1: Quality Overview -->
        <section class="rounded-xl border border-n-weak">
          <div class="flex items-center gap-2 border-b border-n-weak px-6 py-4">
            <span class="i-lucide-bar-chart-3 size-5 text-n-slate-11" />
            <h2 class="text-base font-medium text-n-slate-12">
              Quality Overview
            </h2>
          </div>
          <div class="p-6">
            <div class="grid grid-cols-3 gap-8">
              <!-- Overall score card -->
              <div class="flex flex-col items-center justify-center gap-3">
                <div
                  class="flex size-28 items-center justify-center rounded-full"
                  :class="overallScoreBg"
                >
                  <span class="text-3xl font-bold" :class="overallScoreColor">
                    {{ overallScore.toFixed(1) }}
                  </span>
                </div>
                <div class="flex flex-col items-center gap-1">
                  <span class="text-sm font-medium" :class="overallScoreColor">
                    {{ overallScoreLabel }}
                  </span>
                  <span class="text-xs text-n-slate-10">out of 10.0</span>
                </div>
                <!-- Trend -->
                <div class="flex items-center gap-1.5">
                  <span class="size-4" :class="[trendIcon, trendColor]" />
                  <span class="text-xs" :class="trendColor">
                    {{ trendLabel }}
                  </span>
                </div>
                <!-- Needs review -->
                <div
                  v-if="needsReviewCount > 0"
                  class="mt-1 flex items-center gap-1.5 rounded-lg bg-n-amber-3 px-3 py-1.5"
                >
                  <span
                    class="i-lucide-alert-circle size-3.5 text-n-amber-text"
                  />
                  <span class="text-xs font-medium text-n-amber-text">
                    {{ needsReviewCount }}
                    need{{ needsReviewCount === 1 ? 's' : '' }} review
                  </span>
                </div>
              </div>

              <!-- Dimension bars -->
              <div class="col-span-2 flex flex-col gap-3">
                <div
                  v-for="dim in dimensions"
                  :key="dim.key"
                  class="flex items-center gap-3"
                >
                  <div class="flex w-28 items-center gap-2 shrink-0">
                    <span class="size-4 text-n-slate-11" :class="dim.icon" />
                    <span class="text-sm text-n-slate-12">
                      {{ dim.label }}
                    </span>
                  </div>
                  <div
                    class="flex-1 h-2.5 rounded-full bg-n-solid-3 overflow-hidden"
                  >
                    <div
                      class="h-full rounded-full transition-all duration-500"
                      :class="dimensionBarColor(dim.score)"
                      :style="{ width: `${dim.percentage}%` }"
                    />
                  </div>
                  <span
                    class="w-10 text-right text-sm font-medium text-n-slate-12"
                  >
                    {{ dim.score.toFixed(1) }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </section>

        <!-- SECTION 2: Tool Health -->
        <section class="rounded-xl border border-n-weak">
          <div
            class="flex items-center justify-between border-b border-n-weak px-6 py-4"
          >
            <div class="flex items-center gap-2">
              <span class="i-lucide-wrench size-5 text-n-slate-11" />
              <h2 class="text-base font-medium text-n-slate-12">Tool Health</h2>
            </div>
            <div class="flex items-center gap-4 text-xs text-n-slate-10">
              <span>{{ totalToolCalls }} total calls</span>
              <span v-if="totalToolFailures > 0" class="text-n-ruby-text">
                {{ totalToolFailures }} failures
              </span>
            </div>
          </div>
          <div class="p-6">
            <div
              v-if="toolEntries.length === 0"
              class="flex flex-col items-center justify-center gap-2 py-8"
            >
              <span class="i-lucide-wrench size-8 text-n-slate-9" />
              <span class="text-sm text-n-slate-10">
                No tool activity in this period
              </span>
            </div>
            <div v-else class="grid grid-cols-2 gap-3">
              <div
                v-for="tool in toolEntries"
                :key="tool.rawName"
                class="flex items-center justify-between rounded-lg border border-n-weak px-4 py-3"
              >
                <div class="flex flex-col gap-0.5">
                  <span class="text-sm font-medium text-n-slate-12">
                    {{ tool.name }}
                  </span>
                  <span class="text-xs text-n-slate-10">
                    {{ tool.total }} calls
                    <span v-if="tool.failure > 0" class="text-n-ruby-text">
                      ({{ tool.failure }} failed)
                    </span>
                  </span>
                </div>
                <div class="flex items-center gap-2">
                  <span
                    class="size-2 rounded-full"
                    :class="toolHealthDot(tool.healthPct)"
                  />
                  <span
                    class="rounded-md px-2 py-0.5 text-xs font-medium"
                    :class="[
                      toolHealthColor(tool.healthPct),
                      toolHealthBg(tool.healthPct),
                    ]"
                  >
                    {{ tool.healthPct }}%
                  </span>
                </div>
              </div>
            </div>
          </div>
        </section>

        <!-- SECTION 3: Escalation Summary -->
        <section class="rounded-xl border border-n-weak">
          <div
            class="flex items-center justify-between border-b border-n-weak px-6 py-4"
          >
            <div class="flex items-center gap-2">
              <span class="i-lucide-arrow-up-right size-5 text-n-slate-11" />
              <h2 class="text-base font-medium text-n-slate-12">
                Escalation Summary
              </h2>
            </div>
            <div class="flex items-center gap-4 text-xs text-n-slate-10">
              <span>{{ escalationRate }}% escalation rate</span>
            </div>
          </div>
          <div class="p-6">
            <div class="grid grid-cols-3 gap-6">
              <div
                class="flex flex-col items-center gap-2 rounded-lg border border-n-weak p-4"
              >
                <span class="text-3xl font-bold text-n-slate-12">
                  {{ escalationCount }}
                </span>
                <span class="text-xs text-n-slate-10"> Total Escalations </span>
              </div>

              <div
                class="flex flex-col items-center gap-2 rounded-lg border border-n-weak p-4"
              >
                <span class="text-3xl font-bold text-n-slate-12">
                  {{ escalationRate }}%
                </span>
                <span class="text-xs text-n-slate-10"> Escalation Rate </span>
              </div>

              <div
                class="flex flex-col items-center gap-2 rounded-lg border border-n-weak p-4"
              >
                <span class="text-3xl font-bold text-n-slate-12">
                  {{ stats.total_conversations || 0 }}
                </span>
                <span class="text-xs text-n-slate-10">
                  Total Conversations
                </span>
              </div>
            </div>

            <!-- Top reasons -->
            <div v-if="escalationReasons.length > 0" class="mt-6">
              <h3 class="mb-3 text-sm font-medium text-n-slate-12">
                Top Escalation Reasons
              </h3>
              <div class="flex flex-col gap-2">
                <div
                  v-for="(item, index) in escalationReasons"
                  :key="item.reason"
                  class="flex items-center justify-between rounded-lg bg-n-solid-2 px-4 py-2.5"
                >
                  <div class="flex items-center gap-3">
                    <span
                      class="flex size-5 items-center justify-center rounded-full bg-n-solid-3 text-xs font-medium text-n-slate-11"
                    >
                      {{ index + 1 }}
                    </span>
                    <span class="text-sm text-n-slate-12">
                      {{ item.reason }}
                    </span>
                  </div>
                  <span
                    class="rounded-md bg-n-solid-3 px-2 py-0.5 text-xs font-medium text-n-slate-11"
                  >
                    {{ item.count }}
                  </span>
                </div>
              </div>
            </div>

            <div v-else class="mt-4 flex flex-col items-center gap-2 py-4">
              <span class="i-lucide-check-circle size-6 text-n-teal-text" />
              <span class="text-sm text-n-slate-10">
                No escalations in this period
              </span>
            </div>
          </div>
        </section>
      </div>
    </template>
  </PageLayout>
</template>
